import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface ReviewSignedUploadTarget {
  targetId: string;
  kind: string;
  fileName: string;
  contentType: string;
  filePath: string;
  uploadUrl: string;
  expiresAtUtc: string;
}

export interface ReviewPostGenerationPlan {
  postId: string;
  role?: string;
  resolution?: string;
  length?: number;
  renderMode: 'client';
  handoff: string;
  status: string;
  message?: string;
}

export interface ReviewSocialTargetPlan {
  postId: string;
  targetId: string;
  itemType: string;
  reviewType: 'video' | 'image' | 'post';
  folderPath: string;
  title: string;
  caption?: string | null;
  media?: ReviewSignedUploadTarget | null;
  thumbnail?: ReviewSignedUploadTarget | null;
  script?: ReviewSignedUploadTarget | null;
  titleTarget?: ReviewSignedUploadTarget | null;
  descriptionTarget?: ReviewSignedUploadTarget | null;
  hooksTarget?: ReviewSignedUploadTarget | null;
  metadataTarget: ReviewSignedUploadTarget;
  metadataJson: string;
  scriptText?: string | null;
  titleText?: string | null;
  descriptionText?: string | null;
  hooksJson?: string | null;
}

export interface ReviewSocialTargetComplete {
  postId: string;
  targetId: string;
  reviewId: string;
  reviewType: 'video' | 'image' | 'post';
  driveFileId?: string | null;
}

export interface ReviewTemplateTargetPrepare {
  postId: string;
  targetId: string;
  folderPath: string;
  title: string;
  caption?: string | null;
  scriptText: string;
  descriptionText: string;
  hooksJson: string;
  metadataJson: string;
}

export interface ReviewTemplateRenderUploadPlan {
  postId: string;
  targetId: string;
  folderPath: string;
  video: ReviewSignedUploadTarget;
  thumbnail: ReviewSignedUploadTarget;
}

export interface ReviewImagePostResult {
  postId: string;
  reviewId: string;
  reviewType: 'image';
  assetPath: string;
  reviewImagePath: string;
  directUrl: string;
}

@Injectable({ providedIn: 'root' })
export class ReviewPostGenerationApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/review/social-post`;

  generateAssets(
    postId: string | null,
    useStock: boolean,
    body: Record<string, unknown> = {}
  ): Observable<ReviewPostGenerationPlan> {
    const target = postId && postId !== 'new'
      ? `${this.base}/${encodeURIComponent(postId)}/generate`
      : `${this.base}/generate`;
    return this.http.post<ReviewPostGenerationPlan>(`${target}?useStock=${useStock}`, body);
  }

  reserveReviewTarget(postId: string | null, body: {
    itemType: 'video' | 'image' | 'post' | string;
    title?: string;
    caption?: string;
    contentType?: string;
    fileName?: string;
    thumbnailContentType?: string;
    thumbnailFileName?: string;
    isShort?: boolean;
    durationSeconds?: number | null;
    clientRuntime?: string;
    script?: string;
    hooks?: string[];
    autoReview?: boolean;
  }): Observable<ReviewSocialTargetPlan> {
    const target = postId && postId !== 'new'
      ? `${this.base}/${encodeURIComponent(postId)}/review-targets`
      : `${this.base}/review-targets`;
    return this.http.post<ReviewSocialTargetPlan>(target, body);
  }

  completeReviewTarget(postId: string, targetId: string): Observable<ReviewSocialTargetComplete> {
    return this.http.post<ReviewSocialTargetComplete>(
      `${this.base}/${encodeURIComponent(postId)}/review-targets/${encodeURIComponent(targetId)}/complete`,
      {}
    );
  }

  prepareTemplateReviewTarget(postId: string, body: {
    script: string;
    title?: string | null;
    caption?: string | null;
    templateId?: string | null;
    durationSeconds?: number | null;
  }): Observable<ReviewTemplateTargetPrepare> {
    return this.http.post<ReviewTemplateTargetPrepare>(
      `${this.base}/${encodeURIComponent(postId)}/template-review-targets/prepare`,
      body
    );
  }

  signTemplateRenderUpload(postId: string, targetId: string, body: {
    videoContentType?: string | null;
    thumbnailContentType?: string | null;
  }): Observable<ReviewTemplateRenderUploadPlan> {
    return this.http.post<ReviewTemplateRenderUploadPlan>(
      `${this.base}/${encodeURIComponent(postId)}/template-review-targets/${encodeURIComponent(targetId)}/sign-render-upload`,
      body
    );
  }

  generateImageReview(postId: string, body: {
    source: 'ai' | 'pexels' | 'pixabay' | string;
    promptOrQuery: string;
    style?: string | null;
    resolution?: string | null;
    script?: string | null;
  }): Observable<ReviewImagePostResult> {
    return this.http.post<ReviewImagePostResult>(
      `${this.base}/${encodeURIComponent(postId)}/generate-image-review`,
      body
    );
  }
}
